import React from 'react';
import { Divider, Typography } from '@mui/material';
import useTranslate from '../../../Hooks/useTranslate';
import StyledCircularProgress from '../../Loading/StyledCircularProgress';
import resolveValuePath from '../../../Helpers/resolveValuePath';
import { registryEntry, registryFlag, registryList, LAYOUT } from '../registry';
import { useDetailData } from './DetailData';

/** A rule across the widget, setting one group of fields apart from the next. */
const DividerEntry = function DividerEntry() {
  return <Divider sx={{ my: '8px' }} />;
};

/** A name over a group of fields. */
const HeadingEntry = function HeadingEntry({ entry }) {
  const translate = useTranslate();

  return (
    <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
      {translate(entry?.label ?? '')}
    </Typography>
  );
};

/** Nothing at all, taking the room it is given. */
const SpacerEntry = function SpacerEntry() {
  return null;
};

/**
 * How far through something is, as a ring: `key` counts what is done and `totalKey` what there is
 * to do. `color` and `size` are the ring's.
 */
const ProgressEntry = function ProgressEntry({ entry }) {
  const { record } = useDetailData();

  return (
    <StyledCircularProgress
      progressData={{
        value: resolveValuePath(record, entry?.key),
        total: resolveValuePath(record, entry?.totalKey),
      }}
      label={0}
      color={entry?.color ?? '#49A600'}
      size={entry?.diameter ?? '100px'}
    />
  );
};

/**
 * What an entry of a region can be, and what the editor offers for each.
 *
 * Most are a key of the section drawn by a cell, but a widget is laid out as well as filled: a rule
 * between two groups of figures, a name over them, or a gap holding a place are all things a page
 * needs and none of them reads anything.
 *
 *   usesKey    - names a key of the section, so the editor offers the catalogue for it
 *   usesCell   - drawn by a cell, so the editor offers the cells and their configuration
 *   usesLabel  - carries wording of its own
 *   secondKey  - reads a second key of the section, under the name given here
 *   settings   - what it reads of the entry beyond its key, as `engine/settings.js` declares them
 *   component  - draws it; a kind without one is drawn as a key's value
 */
export const ENTRY_KINDS = [
  {
    kind: 'field',
    label: 'ENTRY_FIELD',
    usesKey: true,
    usesCell: true,
  },
  {
    kind: 'heading',
    label: 'ENTRY_HEADING',
    usesLabel: true,
    component: HeadingEntry,
  },
  { kind: 'divider', label: 'ENTRY_DIVIDER', component: DividerEntry },
  { kind: 'spacer', label: 'ENTRY_SPACER', component: SpacerEntry },
  // Drawn by the grid rather than by a component of its own: what it holds are entries, and the
  // grid is what knows how to draw one.
  {
    kind: 'group',
    label: 'ENTRY_GROUP',
    // A group is laid out rather than drawn: which way its entries run, and how far apart. Its
    // `direction` means that rather than where a caption sits, so it says so itself.
    settings: [
      {
        name: 'direction',
        label: 'GROUP_DIRECTION',
        control: 'choice',
        choices: [
          { value: '', label: 'GROUP_STACKED' },
          { value: 'row', label: 'GROUP_ROW' },
        ],
        category: LAYOUT,
      },
      {
        name: 'gap',
        label: 'GAP',
        control: 'text',
        placeholder: '8px',
        category: LAYOUT,
      },
    ],
  },
  {
    kind: 'progress',
    label: 'ENTRY_PROGRESS',
    usesKey: true,
    // The ring counts one key against another, so the editor offers the second.
    secondKey: { name: 'totalKey', label: 'TOTAL_COLUMN' },
    settings: [
      {
        name: 'diameter',
        label: 'DIAMETER',
        control: 'text',
        placeholder: '100px',
        category: LAYOUT,
      },
      { name: 'color', label: 'COLOR', control: 'colour', category: LAYOUT },
    ],
    component: ProgressEntry,
  },
];

/** What a kind is called when an entry declares none. */
const DEFAULT_KIND = 'field';

/**
 * Whether an entry of this kind names a key of the section.
 *
 * @param {String} kind - An entry's kind.
 *
 * @returns {Boolean}
 */
export const entryUsesKey = (kind) =>
  registryFlag(ENTRY_KINDS, 'kind', kind ?? DEFAULT_KIND, 'usesKey');

/**
 * Whether an entry of this kind is drawn by a cell.
 *
 * @param {String} kind - An entry's kind.
 *
 * @returns {Boolean}
 */
export const entryUsesCell = (kind) =>
  registryFlag(ENTRY_KINDS, 'kind', kind ?? DEFAULT_KIND, 'usesCell');

/**
 * Whether an entry of this kind carries wording of its own.
 *
 * @param {String} kind - An entry's kind.
 *
 * @returns {Boolean}
 */
export const entryUsesLabel = (kind) =>
  registryFlag(ENTRY_KINDS, 'kind', kind ?? DEFAULT_KIND, 'usesLabel');

/**
 * What a kind is called, for an entry that has no wording of its own to be listed by.
 *
 * @param {String} kind - An entry's kind.
 *
 * @returns {String}
 */
export const entryKindLabel = (kind) =>
  registryEntry(ENTRY_KINDS, 'kind', kind ?? DEFAULT_KIND)?.label ?? kind;

/**
 * The second key an entry of this kind reads, as `{ name, label }`, or nothing where it reads one.
 *
 * @param {String} kind - An entry's kind.
 *
 * @returns {Object|null}
 */
export const entrySecondKey = (kind) =>
  registryEntry(ENTRY_KINDS, 'kind', kind ?? DEFAULT_KIND)?.secondKey ?? null;

/**
 * What a kind reads of the entry beyond its key, as the settings the editor offers for it.
 *
 * @param {String} kind - An entry's kind.
 *
 * @returns {Array}
 */
export const entryKindSettings = (kind) =>
  registryList(ENTRY_KINDS, 'kind', kind ?? DEFAULT_KIND, 'settings');

/**
 * What draws an entry of this kind, or nothing where its key's value is what is drawn.
 *
 * @param {String} kind - An entry's kind.
 *
 * @returns {Function|null}
 */
export const entryComponent = (kind) =>
  registryEntry(ENTRY_KINDS, 'kind', kind ?? DEFAULT_KIND)?.component ?? null;

/**
 * Adds a page's own kinds of entry to the registry.
 *
 * The kinds above are the ones any section can use, since they draw a value or draw nothing. One
 * that knows what it is for - a chooser of the customer's email addresses - belongs to the page
 * that owns it and is declared there, so the engine holds no page's components.
 *
 * Called once, from the module the page imports.
 *
 * @param {Array} declarations - Entries as ENTRY_KINDS holds them, each with a `component`.
 */
export const registerEntryKinds = (declarations) =>
  (declarations ?? []).forEach((declaration) => {
    if (!declaration.kind || !declaration.component) return;

    const held = ENTRY_KINDS.findIndex(
      (entry) => entry.kind === declaration.kind
    );

    if (held >= 0) ENTRY_KINDS.splice(held, 1, declaration);
    else ENTRY_KINDS.push(declaration);
  });
